import React from 'react'
import { useWatch } from 'react-hook-form'
import { FormField } from '../forms/FormField'
import { SelectField } from '../forms/SelectField'
import { MORE_SAME_LESS_OPTIONS, YES_NO_OPTIONS } from './profileStepOptions'
import type { FnaFormValues } from '../../contracts/fnaFormSchema'

/**
 * Step 3: Retirement target age and contributions.
 *
 * @returns JSX.Element The retirement step
 */
export function RetirementStep(): JSX.Element {
  const withSpouse = useWatch<FnaFormValues, 'withSpouse'>({ name: 'withSpouse' })
  const additionalRetireIncome = useWatch<FnaFormValues, 'additionalRetireIncome'>({
    name: 'additionalRetireIncome',
  })
  const previousSavings = useWatch<FnaFormValues, 'previousSavings'>({ name: 'previousSavings' })
  return (
    <>
      <h2>Retirement</h2>
      <FormField<FnaFormValues>
        name="retireAge"
        label="Desired retirement age"
        type="number"
        placeholder="65"
        inputMode="numeric"
      />
      <SelectField<FnaFormValues>
        name="retirementIncome"
        label="In retirement, do you expect to need more, the same, or less income than today?"
        options={MORE_SAME_LESS_OPTIONS}
      />
      <SelectField<FnaFormValues>
        name="additionalRetireIncome"
        label="Will you have any additional retirement income (e.g. pension, rental, part-time work)?"
        options={YES_NO_OPTIONS}
      />
      {additionalRetireIncome === 'yes' && (
        <>
          <FormField<FnaFormValues>
            name="sourceOfIncome"
            label="Source of additional retirement income"
            placeholder="e.g. pension, rental income"
          />
          <FormField<FnaFormValues>
            name="monthlyAmount"
            label="Expected monthly amount"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
        </>
      )}
      <SelectField<FnaFormValues>
        name="previousSavings"
        label="Do you have retirement savings from a previous employer?"
        options={YES_NO_OPTIONS}
      />
      {previousSavings === 'yes' && (
        <SelectField<FnaFormValues>
          name="rolledOver"
          label="Have those previous savings been rolled over?"
          options={YES_NO_OPTIONS}
        />
      )}
      <FormField<FnaFormValues>
        name="totalRetirementSavings"
        label="Total retirement savings"
        type="number"
        placeholder="0"
        inputMode="decimal"
      />
      <FormField<FnaFormValues>
        name="clientMonthlyContribution"
        label="Your monthly contribution"
        type="number"
        placeholder="0"
        inputMode="decimal"
      />
      <FormField<FnaFormValues>
        name="clientEmployerContribution"
        label="Your employer's monthly contribution"
        type="number"
        placeholder="0"
        inputMode="decimal"
      />
      {withSpouse === 'yes' && (
        <>
          <FormField<FnaFormValues>
            name="spouseMonthlyContribution"
            label="Spouse's monthly contribution"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
          <FormField<FnaFormValues>
            name="spouseEmployerContribution"
            label="Spouse's employer monthly contribution"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
        </>
      )}
    </>
  )
}
